import React, {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";

import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  useGridApiRef,
  GridRowModes,
  GridRowModesModel,
  GridRowEditStartParams,
  GridRowEditStartReasons,
  GridRowEditStopParams,
  GridRowEditStopReasons,
  GridRowSelectionModel,
  GridEventListener,
  MuiEvent,
} from "@mui/x-data-grid-pro";

import {
  OrganizationResponse,
  BuildingResponse,
  UserResponse,
  WebAssetResponse,
  WebFieldsResponse,
} from "@akitabox/api-client";
import { makeUseServiceCall } from "../hooks/useServiceCall";
import { api } from "../api";
import { useRouteLoaderData } from "react-router";
import { useLocation, useSearchParams } from "react-router-dom";

import { useInfiniteCall } from "../hooks/useInfiniteCall";
import { useApiMutation } from "../hooks/useApiMutation";

import { Box, Snackbar } from "@mui/material";

// import { unstable_useEnhancedEffect as useEnhancedEffect } from "@mui/utils";

import ActiveViewToolbar from "./ActiveViewToolbar";
import GridToolbar from "./GridToolbar";
import ListPagination from "../components/ListPagination";
import ExportDrawer from "./ExportDrawer";
import { AttachmentsPreview } from "./AttachmentsPreview";
import { LocationPreview } from "./LocationPreview";
import SideDrawer from "./SideDrawer";
import { stylesheet } from "../stylesheet";
import { FilterTerms, DetailSettings } from "./types";
import { useAngularApp } from "../legacy/angularApp";
import {
  handleLegacyAddServiceRequest,
  handleLegacyAddWorkOrder,
  handleLegacyAddMaintenanceSchedule,
  handleLegacyDecommission,
  handleLegacyDelete,
  handleLegacyCondition,
  handleLegacyLifeCycleSelection,
} from "./assetActionsUtils";
import { useListContext } from "./ListContext";
import {
  getActionColumns,
  getBaseColumns,
  getCreatedColumns,
  getCustomColumns,
  getDetailColumns,
  getModifiedColumns,
} from "./columns";
import SelectAllDialog from "./SelectAllDialog";
import ImportDrawer from "./ImportDrawer";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment";

const ss = stylesheet({
  dataGridWrapper: {
    ".MuiDataGrid-virtualScroller": {
      overflowY: "visible!important" as any,
    },
    background: "#ffffff",
    paddingTop: "20px",
    paddingLeft: "10px",
    paddingRight: "10px",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "scroll",
  },
  container: {
    display: "flex",
    flexDirection: "row",
    height: "calc(100% - 64px)", // Adjust based on the height of the toolbar
  },
  dataGridContainer: {
    flex: 1,
    overflow: "auto",
  },
  sideDrawerContainer: {
    overflow: "auto",
    position: "relative",
    zIndex: 0,
  },
  relativeDrawer: {
    position: "relative",
  },
});
export interface AssetSelectionDataGridFilters {
  values?: any;
  name?: string;
  pinType?: string;
  building?: string;
  level?: string;
  condition?: string;
  total_cost_with_adjustments?: string;
  has_attachments?: boolean;
  decommissioned?: boolean;
  installation_date?: string;
  created_date?: string;
  modified_date?: string;
  "pinType.name"?: string;
  "room.number"?: string;
  "room.name"?: string;
}

export interface AssetListProps {
  filters?: any;
  onFilterChange?: (filters: AssetSelectionDataGridFilters) => void;
}

function operatorToQuery(operator: string) {
  switch (operator) {
    case "is":
    case "equals":
      return "$eq";
    case "notEquals":
      return "$ne";
    case ">":
    case "after":
      return "$gt";
    case ">=":
      return "$gte";
    case "<":
    case "before":
      return "$lt";
    case "<=":
      return "$lte";
    case "contains":
      return "$regex";
    case "isEmpty":
      return "$exists,false";
    case "isNotEmpty":
      return "$exists,true";
    default:
      return "$eq";
  }
}

function queryToOperator(query: string) {
  switch (query) {
    case "$eq":
      return "equals";
    case "$ne":
      return "notEquals";
    case "$gt":
      return ">";
    case "$gte":
      return ">=";
    case "$lt":
      return "<";
    case "$lte":
      return "<=";
    case "$regex":
      return "contains";
    case "$exists,false":
      return "isEmpty";
    case "$exists,true":
      return "isNotEmpty";
    case "$in":
      return "is";
    case "$nin":
      return "not";
    default:
      return query;
  }
}

const BaseExportFieldIds = {
  name: "_id",
  "building.name": "Building _id",
  "pinType.name": "Asset Category _id",
  "level.name": "Floor _id",
  "room.number": "Room _id",
};
const BaseExportFields = {
  "building.name": "Building Name",
  "pinType.name": "Asset Category Name",
  name: "Asset Name",
  installation_date: "Installation Date",
  percentX: "Percent X",
  percentY: "Percent Y",
  size: "Size",
  size_unit: "Size Unit",
  quantity: "Quantity",
  quantity_unit: "Quantity Unit",
  "level.name": "Floor Name",
  "room.number": "Room Number",
  "room.name": "Room Name",
  "costlines.total_cost": "Total Cost",
  "costlines.total_cost_with_adjustments": "Total Cost with Adjustments",
  condition: "Condition",
  created_date: "Created Date",
  modified_date: "Modified Date",
};
const BaseExportExtended = {
  name: ["Asset URL", "Number"],
};

export const AssetList: FunctionComponent<AssetListProps> = () => {
  const apiRef = useGridApiRef();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshList, setRefreshList } = useListContext();

  const { organization, user, flags } = useRouteLoaderData("shell") as {
    organization: OrganizationResponse;
    user: UserResponse;
    flags: any;
  };

  const permissions = useMemo(
    () => ({
      canDecommissionAsset: user.permission_group.asset["decommission"],
      canRecommissionAsset: user.permission_group.asset["recommission"],
      canDeleteAsset: user.permission_group.asset["remove"],
      canAddRequest:
        !!organization.show_tasks &&
        !!(user.permission_group.request["create"] ?? false),
      canAddWorkOrder:
        !!organization.show_tasks &&
        !!(user.permission_group.task["create"] ?? false),
      canAddSchedule:
        !!organization.show_tasks &&
        !!(user.permission_group.future_task["create"] ?? false),
      canManageAssociations: user.permission_group.association["create"],
      canManageCostAdjustments:
        flags["asset_life_cycle"] &&
        user.permission_group.cost_adjustment["create"] &&
        user.permission_group.cost_adjustment["remove"] &&
        user.permission_group.cost_adjustment["update"],
      canManageLifeCycle:
        organization.show_fca &&
        flags["asset_life_cycle"] &&
        user.permission_group.cost_line["create"] &&
        user.permission_group.cost_line["update"] &&
        user.permission_group.cost_line["remove"],
      canUpdateAsset: user.permission_group.asset["update"],
    }),
    [user.permission_group, organization, flags]
  );
  const { asset: list_view } = user.permission_group;
  const assetPermissions = useMemo(
    () => ({
      read: list_view["read"],
      update: list_view["update"],
    }),
    [list_view]
  );

  const allowedPageSizes = [25, 50, 100];

  const [filters, setFilters] = useState<AssetSelectionDataGridFilters>();
  const [showHeaderFilters, setShowHeaderFilters] = useState<boolean>(false);
  const [visibleFilterTerms, setVisibleFilterTerms] = useState<FilterTerms[]>(
    []
  );
  const [isColumnChanged, setIsColumnChanged] = useState<boolean>(false);
  const [isFilterChanged, setIsFilterChanged] = useState<boolean>(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const defaultSettings: DetailSettings = {
    showAttachments: {
      state: true,
      displayName: "Show Attachments",
      iconName: "PhotoLibrary",
    },
    showLocation: {
      state: true,
      displayName: "Show Locations",
      iconName: "Place",
    },
    // showAssociations: {
    //   state: false,
    //   displayName: "Show Associations",
    //   iconName: "AccountTree",
    // },
    showTotalCostWithAdjustments: {
      state: false,
      displayName: "Show Total Cost (Adjusted)",
      iconName: "CurrencyExchange",
    },
    showConditions: {
      state: false,
      displayName: "Show Conditions",
      iconName: "Grading",
    },
  };
  const getInitialSettings = (): DetailSettings => {
    const storedSettings = localStorage.getItem("assetlist_detail");
    let settings = storedSettings
      ? JSON.parse(storedSettings)
      : defaultSettings;

    // Migration step: Add showTotalCostWithAdjustments if it is missing
    if (!settings.showTotalCostWithAdjustments) {
      settings = {
        ...settings,
        showTotalCostWithAdjustments:
          defaultSettings.showTotalCostWithAdjustments,
      };
      localStorage.setItem("assetlist_detail", JSON.stringify(settings));
    }
    // Migration step: Add showConditions if it is missing
    if (!settings.showConditions) {
      settings = {
        ...settings,
        showConditions: defaultSettings.showConditions,
      };
      localStorage.setItem("assetlist_detail", JSON.stringify(settings));
    }
    return settings;
  };
  const [detailSettings, setDetailSettings] = useState<DetailSettings>(
    getInitialSettings()
  );
  const updateSetting = (settingName: string, newState: boolean) => {
    setDetailSettings((prevSettings) => ({
      ...prevSettings,
      [settingName]: { ...prevSettings[settingName], state: newState },
    }));
  };

  const getInitialSidebarOpen = (): boolean => {
    const storedSidebarOpen = localStorage.getItem("assetlist_sidebar");
    return storedSidebarOpen ? JSON.parse(storedSidebarOpen) : false;
  };
  const [sidebarOpen, setSidebarOpen] = useState(getInitialSidebarOpen());
  const updateSidebarSetting = (newState: boolean) => {
    setSidebarOpen(newState);
    if (newState) {
      setDrawer("side");
    } else {
      // Only close the drawer if it's currently set to "side"
      setDrawer((prev) => (prev === "side" ? null : prev));
    }
  };

  const [checkboxVisible, setCheckboxVisible] = useState(false);
  const [showDecommissioned, setShowDecommissioned] = useState(false);
  const updateDecommissionedSetting = (newState: boolean) => {
    if (newState !== showDecommissioned) {
      filterModel.items.push({
        field: "decommissioned",
        operator: "equals",
        value: newState,
      });
      setFilterModel(filterModel);
      onAssetFilterChange(filterModel);
    }
    setShowDecommissioned(newState);
  };
  const [drawer, setDrawer] = useState<null | "side" | "export" | "import">(
    null
  );
  const handleToggleCheckboxSetting = () => {
    setCheckboxVisible((prev) => !prev);
  };

  useEffect(() => {
    localStorage.setItem("assetlist_detail", JSON.stringify(detailSettings));
    localStorage.setItem("assetlist_sidebar", JSON.stringify(sidebarOpen));
  }, [detailSettings, sidebarOpen]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: allowedPageSizes[0],
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "name", sort: "asc" },
  ]);

  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const handleSaveClick = useCallback(
    (assetId: string) => {
      const rowModes = { ...rowModesModel };
      rowModes[assetId] = { mode: GridRowModes.View };
      setRowModesModel(rowModes);
    },
    [rowModesModel]
  );
  const handleEditClick = useCallback(
    (assetId: string) => {
      const rowModes = { ...rowModesModel };
      rowModes[assetId] = { mode: GridRowModes.Edit };
      setRowModesModel(rowModes);
    },
    [rowModesModel]
  );
  const handleCancelClick = useCallback(
    (assetId: string) => () => {
      const rowModes = { ...rowModesModel };
      rowModes[assetId] = {
        mode: GridRowModes.View,
        ignoreModifications: true,
      };
      setRowModesModel(rowModes);
    },
    [rowModesModel]
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [currentPreview, setCurrentPreview] = useState(""); // or 'location', or null
  const [attachmentAsset, setAttachmentAsset] = useState<any>();
  const handleAttachmentsPreviewOpen = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement | HTMLLIElement>,
      asset: any
    ) => {
      setAnchorEl(event.currentTarget);
      setAttachmentAsset(asset);
      setCurrentPreview("attachments");
    },
    []
  );
  const handleAttachmentsPreviewClose = () => {
    setAnchorEl(null);
    setAttachmentAsset("");
    setCurrentPreview("");
  };

  const [locationAsset, setLocationAsset] = useState<any>();
  const handleLocationPreviewOpen = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement | HTMLLIElement>,
      asset: any
    ) => {
      setAnchorEl(event.currentTarget);
      setLocationAsset(asset);
      setCurrentPreview("location");
    },
    []
  );
  const handleLocationPreviewClose = () => {
    setAnchorEl(null);
    setLocationAsset("");
    setCurrentPreview("");
  };

  // Find all of the buildings in the organization
  const { data: buildingResponse } = makeUseServiceCall(
    api.buildings.getByOrganization
  )({
    organizationId: organization._id,
    skip: 0,
    limit: 1000,
  });
  const buildings = useMemo(() => {
    return buildingResponse ? buildingResponse.data : [];
  }, [buildingResponse]);
  const buildingOptions = useMemo(() => {
    return buildings
      .sort((a, b) => a?.name?.localeCompare(b?.name))
      .map((building) => ({
        label: building?.name,
        value: building?._id,
      }));
  }, [buildings]);

  // pin_type_names
  const { data: pinTypesResponse } = makeUseServiceCall(
    api.web.getPinTypeNames
  )({
    organizationId: organization._id,
    skip: 0,
    limit: 1000,
  });
  const pinTypeNames = useMemo(() => {
    return pinTypesResponse ? pinTypesResponse.data : [];
  }, [pinTypesResponse]);
  const pinTypeOptions = useMemo(() => {
    return Array.isArray(pinTypeNames)
      ? pinTypeNames
          .filter((pinType: any) => pinType?._id !== "Room")
          .map((pinType: any) => ({
            label: pinType?._id,
            value: pinType?._id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];
  }, [pinTypeNames]);

  const { data: pinFieldsCountResponse } = makeUseServiceCall(
    api.web.getFieldsCount
  )({
    organizationId: organization._id,
  });
  const pinFieldsCount = useMemo(() => {
    return pinFieldsCountResponse ? pinFieldsCountResponse.data.count : 0;
  }, [pinFieldsCountResponse]);

  const [pinFields, setPinFields] = useState<WebFieldsResponse[]>([]);
  useEffect(() => {
    const fetchAllPinFields = async () => {
      if (pinFieldsCountResponse) {
        let allPinFields: WebFieldsResponse[] = [];
        while (allPinFields.length < pinFieldsCount) {
          const { data: pintFieldsResponse } = await api.web.getFields({
            organizationId: organization._id,
            skip: allPinFields.length,
            limit: 1000,
          });
          allPinFields = [
            ...allPinFields,
            ...(Array.isArray(pintFieldsResponse) ? pintFieldsResponse : []),
          ];
        }
        setPinFields(allPinFields);
      }
    };
    fetchAllPinFields();
  }, [pinFieldsCountResponse, organization._id, pinFieldsCount]);

  const uniquePinTypeFields = useMemo(() => {
    return Array.from(new Set(pinFields.map((pinField) => pinField.name)));
  }, [pinFields]);

  // Find all of the Levels in the organization
  const { data: orgLevelsResponse } = makeUseServiceCall(
    api.levels.byOrganization
  )({
    organizationId: organization._id,
    skip: 0,
    limit: 1000,
  });
  const orgLevels = useMemo(() => {
    return orgLevelsResponse ? orgLevelsResponse.data : [];
  }, [orgLevelsResponse]);
  const orgLevelsOptions = useMemo(() => {
    function getBuildingName(level: any) {
      return buildings.find((building) => building._id === level.building)
        ?.name;
    }
    const levelOptions = orgLevels
      .map((level) => ({
        label: level?.name,
        value: level._id,
        building: level.building,
        group: getBuildingName(level),
      }))
      .sort((a, b) => a?.label.localeCompare(b?.label));
    if (filterModel) {
      const items = filterModel.items;
      const buildingFilter = items.find(
        ({ field }) => field === "building.name"
      );
      if (buildingFilter && buildingFilter.value) {
        const buildingId = buildingFilter.value;
        if (buildingId) {
          return levelOptions.filter(
            (option) => option.building === buildingId
          );
        }
      }
    }
    return levelOptions;
  }, [orgLevels, buildings, filterModel]);

  const conditionOptions = useMemo(() => {
    return [
      { label: "Very Good", value: "Very Good" },
      { label: "Good", value: "Good" },
      { label: "Fair", value: "Fair" },
      { label: "Poor", value: "Poor" },
      { label: "Very Poor", value: "Very Poor" },
      { label: "Failing", value: "Failing" },
    ];
  }, []);

  const onSortModelChange = useCallback((sortModel: GridSortModel) => {
    if (!sortModel.length) {
      setSortModel([{ field: "name", sort: "asc" }]);
      return;
    }
    const sortItem = sortModel[0];
    if (!sortItem) return;
    let field = sortItem.field;
    if (field === "building") {
      field = "building.name";
    } else if (field === "level") {
      field = "level.name";
    } else if (field === "room") {
      field = "room.number";
    }

    setSortModel([
      {
        field: field,
        sort: sortItem.sort,
      },
    ]);
  }, []);

  const injector = useAngularApp();

  const addServiceRequest = useCallback(
    (
      organization: OrganizationResponse,
      building: BuildingResponse,
      assets: WebAssetResponse[]
    ) =>
      handleLegacyAddServiceRequest(injector, organization, building, assets),
    [injector]
  );
  const addWorkOrder = useCallback(
    (
      organization: OrganizationResponse,
      building: BuildingResponse,
      assets: WebAssetResponse[]
    ) => handleLegacyAddWorkOrder(injector, organization, building, assets),
    [injector]
  );
  const addMaintenanceSchedule = useCallback(
    (
      organization: OrganizationResponse,
      building: BuildingResponse,
      assets: WebAssetResponse[]
    ) =>
      handleLegacyAddMaintenanceSchedule(
        injector,
        organization,
        building,
        assets
      ),
    [injector]
  );
  const decommissionAsset = useCallback(
    (assets: WebAssetResponse[]) => {
      handleLegacyDecommission(injector, assets).then(() => {
        setRefreshList(true);
      });
    },
    [injector, setRefreshList]
  );
  const deleteAsset = useCallback(
    (assets: WebAssetResponse[]) => {
      handleLegacyDelete(injector, assets).then(() => {
        setRefreshList(true);
      });
    },
    [injector, setRefreshList]
  );
  const addConditionRating = useCallback(
    (asset: WebAssetResponse) => {
      handleLegacyCondition(injector, asset).then(() => {
        setTimeout(() => {
          setRefreshList(true);
        }, 1000);
      });
    },
    [injector, setRefreshList]
  );
  const addLifeCycleCost = useCallback(
    (asset: WebAssetResponse) => {
      handleLegacyLifeCycleSelection(injector, [asset]).then(() => {
        setTimeout(() => {
          setRefreshList(true);
        }, 1000);
      });
    },
    [injector, setRefreshList]
  );
  const [attachmentFilterState, setAttachmentFilterState] = useState<
    boolean | null
  >(null);

  const [columnsLoaded, setColumnsLoaded] = useState(false);

  const baseColumns = useMemo(() => {
    return getBaseColumns({
      orgLevelsOptions,
      buildingOptions,
      pinTypeOptions,
      conditionOptions,
      detailSettings,
      organization,
    });
  }, [
    orgLevelsOptions,
    buildingOptions,
    pinTypeOptions,
    conditionOptions,
    detailSettings,
    organization,
  ]);
  const detailColumns = useMemo(() => {
    return getDetailColumns({
      detailSettings,
      attachmentFilterState,
      setAttachmentFilterState,
      apiRef,
      flags,
      addConditionRating,
      addLifeCycleCost,
      handleAttachmentsPreviewOpen,
      handleLocationPreviewOpen,
      organization,
    });
  }, [
    detailSettings,
    attachmentFilterState,
    setAttachmentFilterState,
    apiRef,
    flags,
    addConditionRating,
    addLifeCycleCost,
    handleAttachmentsPreviewOpen,
    handleLocationPreviewOpen,
    organization,
  ]);
  const customColumns = useMemo(() => {
    return getCustomColumns({
      uniquePinTypeFields,
      pinFields,
      buildingOptions,
    });
  }, [uniquePinTypeFields, pinFields, buildingOptions]);
  const createdColumns = useMemo(() => {
    return getCreatedColumns();
  }, []);
  const modifiedColumns = useMemo(() => {
    return getModifiedColumns();
  }, []);
  const actionColumns = useMemo(() => {
    return getActionColumns({
      addMaintenanceSchedule,
      addServiceRequest,
      addWorkOrder,
      assetPermissions,
      decommissionAsset,
      deleteAsset,
      handleCancelClick,
      handleEditClick,
      handleSaveClick,
      organization,
      permissions,
      rowModesModel,
    });
  }, [
    addMaintenanceSchedule,
    addServiceRequest,
    addWorkOrder,
    assetPermissions,
    decommissionAsset,
    deleteAsset,
    handleCancelClick,
    handleEditClick,
    handleSaveClick,
    organization,
    permissions,
    rowModesModel,
  ]);

  const [showCustomColumns, setShowCustomColumns] = useState(false);

  // Column order state must be declared before it's used in columns useMemo
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [isColumnOrderChanged, setIsColumnOrderChanged] =
    useState<boolean>(false);

  const baseColumnsArray: GridColDef<WebAssetResponse>[] = useMemo(() => {
    const columns = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
      },
      ...baseColumns,
      ...customColumns,
      ...createdColumns,
      ...modifiedColumns,
      ...detailColumns,
      ...actionColumns,
    ];
    setColumnsLoaded(true);

    return columns;
  }, [
    baseColumns,
    customColumns,
    createdColumns,
    modifiedColumns,
    detailColumns,
    actionColumns,
  ]);

  // Apply column order if saved in view
  const columns: GridColDef<WebAssetResponse>[] = useMemo(() => {
    if (columnOrder.length === 0) {
      return baseColumnsArray;
    }

    // Create a map for quick lookup
    const columnMap = new Map(baseColumnsArray.map((col) => [col.field, col]));

    // Reorder based on saved order, then append any missing columns
    const orderedCols: GridColDef<WebAssetResponse>[] = [];
    const usedFields = new Set<string>();

    // Add columns in the saved order
    columnOrder.forEach((field) => {
      const col = columnMap.get(field);
      if (col) {
        orderedCols.push(col);
        usedFields.add(field);
      }
    });

    // Add any columns that weren't in the saved order
    baseColumnsArray.forEach((col) => {
      if (!usedFields.has(col.field)) {
        orderedCols.push(col);
      }
    });

    return orderedCols;
  }, [baseColumnsArray, columnOrder]);

  const initialColumnVisibilityModel = useMemo(() => {
    const model: { [key: string]: boolean } = {}; // Add an index signature to the model object
    baseColumnsArray.forEach((column: any) => {
      model[column.field] = column.field.startsWith("values.") ? false : true;
    });
    return model;
  }, [baseColumnsArray]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<{
    [key: string]: boolean;
  }>(initialColumnVisibilityModel);

  const initialExportColumns = useMemo(() => {
    // Use the ordered columns array to preserve column order in exports
    const exportColumns = columns
      .filter((col) => columnVisibilityModel[col.field] !== false)
      .reduce<string[]>((acc, col) => {
        const key = col.field;
        if ((BaseExportFields as Record<string, any>)[key]) {
          acc.push((BaseExportFields as Record<string, any>)[key]);
        }
        if ((BaseExportFieldIds as Record<string, any>)[key]) {
          acc.push((BaseExportFieldIds as Record<string, any>)[key]);
        }
        if ((BaseExportExtended as Record<string, any>)[key]) {
          // Note this is an array of strings
          acc.push(...(BaseExportExtended as Record<string, any>)[key]);
        } else {
          if (key.startsWith("values.")) {
            // remove the "values." prefix
            acc.push(key.replace("values.", ""));
          }
        }
        return acc;
      }, []);
    return exportColumns;
  }, [columns, columnVisibilityModel]);

  const [exportColumns, setExportColumns] =
    useState<string[]>(initialExportColumns);

  useEffect(() => {
    setExportColumns(initialExportColumns);
  }, [initialExportColumns]);

  const prevShowCustomColumnsRef = useRef<boolean | undefined>(undefined);
  const prevColumns = useRef(baseColumnsArray);

  useEffect(() => {
    if (
      prevShowCustomColumnsRef.current === showCustomColumns &&
      prevColumns.current.length === baseColumnsArray.length
    )
      return;
    const model: { [key: string]: boolean } = {}; // Add an index signature to the model object
    baseColumnsArray.forEach((column: any) => {
      model[column.field] = column.field.startsWith("values.")
        ? showCustomColumns
        : true;
    });
    setColumnVisibilityModel(model);
    prevShowCustomColumnsRef.current = showCustomColumns;
    prevColumns.current = baseColumnsArray;
  }, [baseColumnsArray, showCustomColumns]);

  const handleCustomColumnsClick = useCallback((state: boolean) => {
    setShowCustomColumns(state);
  }, []);

  const handleColumnOrderChange = useCallback(
    (_params: any) => {
      // Get the current column order from the API after reorder
      const currentOrder = apiRef.current
        .getAllColumns()
        .map((col) => col.field);
      setColumnOrder(currentOrder);
      setIsColumnOrderChanged(true);
    },
    [apiRef, setColumnOrder, setIsColumnOrderChanged]
  );

  const {
    data: assetsQueryResponse,
    isLoading,
    isValidating,
    mutate,
  } = useInfiniteCall(
    api.web.getAssets,
    () => [
      {
        sort: sortModel.map((item) => `${item.field},${item.sort}`).join(","),
        orgId: organization._id,
        decommissioned: filters?.decommissioned,
        name: filters?.name,
        building: filters?.building,
        pinType: filters?.pinType,
        values: filters?.values,
        level: filters?.level,
        hasAttachments: filters?.has_attachments,
        conditionConditionRating: filters?.condition,
        costlinesTotalCostWithAdjustments: filters?.total_cost_with_adjustments,
        installationDate: filters?.installation_date,
        createdDate: filters?.created_date,
        modifiedDate: filters?.modified_date,
        pinTypeName: filters?.["pinType.name"],
        roomName: filters?.["room.name"],
        roomNumber: filters?.["room.number"],
        // pagination
        skip: paginationModel.page * paginationModel.pageSize,
        limit: paginationModel.pageSize,
      },
    ],
    uniquePinTypeFields.length > 0
  );
  useEffect(() => {
    mutate();
    if (refreshList) {
      setRefreshList(false);
    }
  }, [mutate, refreshList, setRefreshList]);

  const assets = useMemo(() => {
    if (!assetsQueryResponse || assetsQueryResponse[0] === undefined) return [];
    const flattenedItems = assetsQueryResponse.flatMap((item) => item.data);
    return flattenedItems;
  }, [assetsQueryResponse]);
  const { trigger: updateAsset } = useApiMutation(api.assets.update);

  const { data: assetsCountResponse, isLoading: isCounting } =
    makeUseServiceCall(api.web.getAssetsCount)({
      orgId: organization._id,
      decommissioned: filters?.decommissioned,
      name: filters?.name,
      building: filters?.building,
      pinType: filters?.pinType,
      values: filters?.values,
      level: filters?.level,
      hasAttachments: filters?.has_attachments,
      conditionConditionRating: filters?.condition,
      costlinesTotalCostWithAdjustments: filters?.total_cost_with_adjustments,
      installationDate: filters?.installation_date,
      createdDate: filters?.created_date,
      modifiedDate: filters?.modified_date,
      pinTypeName: filters?.["pinType.name"],
      roomName: filters?.["room.name"],
      roomNumber: filters?.["room.number"],
    });
  const assetCount = useMemo(() => {
    if (!assetsCountResponse) return 0;
    return assetsCountResponse.data.count;
  }, [assetsCountResponse]);

  useEffect(() => {
    const getColumnHeaderName = (field: string) => {
      const column = columns.find((col) => col.field === field);
      return column ? column.headerName : field;
    };
    const terms: FilterTerms[] = [];
    const filterHasChanges = filterModel?.items?.length > 0;
    setIsFilterChanged(filterHasChanges);

    filterModel?.items?.forEach(({ field, value, operator }) => {
      if (!value && operator !== "isEmpty" && operator !== "isNotEmpty") {
        return;
      }
      let description = getColumnHeaderName(field);
      let valueDescription = value;
      let operatorDescription = "";
      if (field === "building.name") {
        description = `Building`;
        valueDescription = Array.isArray(value)
          ? value.map(
              (buildingId: string) =>
                buildings.find((building) => building._id === buildingId)?.name
            )
          : buildings.find((building) => building._id === value)?.name;
      } else if (field === "level.name") {
        valueDescription = orgLevels.find((level) => level._id === value)?.name;
      } else if (field === "Details") {
        description = `Has Attachments:`;
        valueDescription = `${value.charAt(0).toUpperCase() + value.slice(1)}`;
      } else if (field === "condition") {
        valueDescription = conditionOptions.find(
          (condition) => condition.value === value
        )?.label;
      } else if (field === "created_date" || field === "modified_date") {
        // Format date fields in local timezone
        if (value instanceof Date) {
          valueDescription = value.toUTCString();
        } else if (Array.isArray(value) && value.length === 2) {
          // Handle range queries
          const start =
            value[0] instanceof Date ? value[0].toUTCString() : value[0];
          const end =
            value[1] instanceof Date ? value[1].toUTCString() : value[1];
          valueDescription = `${start} - ${end}`;
        }
      } else if (field === "installation_date") {
        // Installation date is year-only
        if (value instanceof Date) {
          valueDescription = value.getFullYear().toString();
        }
      }

      switch (operator) {
        case "after":
          operatorDescription = `After`;
          break;
        case "before":
          operatorDescription = `Before`;
          break;
        case "onOrAfter":
          operatorDescription = `On or After`;
          break;
        case "onOrBefore":
          operatorDescription = `On or Before`;
          break;
        case "between":
          operatorDescription = `Between`;
          break;
        case "<":
          operatorDescription = `Less Than`;
          break;
        case "<=":
          operatorDescription = `Less Than or Equal To`;
          break;
        case ">":
          operatorDescription = `Greater Than`;
          break;
        case ">=":
          operatorDescription = `Greater Than or Equal To`;
          break;
        case "not":
          operatorDescription = `Is Not`;
          break;
        case "isEmpty":
          operatorDescription = `Is Empty`;
          valueDescription = "";
          break;
        case "isNotEmpty":
          operatorDescription = `Is Not Empty`;
          valueDescription = "";
          break;
        default:
          operatorDescription = "";
          break;
      }

      description = `${description}: ${operatorDescription} ${valueDescription}`;
      terms.push({
        [field]: value,
        filterItem: field,
        description,
      });
    });
    setVisibleFilterTerms(terms);
  }, [buildings, filterModel, orgLevels, conditionOptions, columns]);

  const onAssetFilterChange = useCallback(
    async (newFilterModel: GridFilterModel) => {
      const items = newFilterModel.items ? newFilterModel.items : [];
      const filters: AssetSelectionDataGridFilters = {};
      const searchParams: { [key: string]: any } = {};
      const nameFilter = items.find(({ field }) => field === "name");
      if (nameFilter && nameFilter.value) {
        filters.name = nameFilter.value;
        searchParams.name = nameFilter.value;
      }

      const buildingFilter = items.find(
        ({ field }) => field === "building.name"
      );
      if (buildingFilter && buildingFilter.value) {
        // filters.building = buildingFilter.value;
        if (
          Array.isArray(buildingFilter.value) &&
          buildingFilter.operator === "isAnyOf"
        ) {
          // For each value in the array of buildingFilter.value, get the id of the pinType
          // and join them together with a comma
          const ids = buildingFilter.value.join(",");
          filters.building = `$in,${ids}`;
        } else {
          const ids = buildingFilter.value;
          filters.building =
            buildingFilter.operator === "not" ? `$nin,${ids}` : `$in,${ids}`;
        }
        searchParams.building = filters.building;
      }

      const pinTypeFilter = items.find(({ field }) => field === "pinType.name");
      if (pinTypeFilter && pinTypeFilter.value) {
        if (
          Array.isArray(pinTypeFilter.value) &&
          pinTypeFilter.operator === "isAnyOf"
        ) {
          filters["pinType.name"] = pinTypeFilter.value.join(",");
        } else {
          filters["pinType.name"] =
            pinTypeFilter.operator === "not"
              ? `$nin,${pinTypeFilter.value}`
              : `$in,${pinTypeFilter.value}`;
        }
        searchParams.pinTypeName = filters["pinType.name"];
      }

      const roomNumberFilter = items.find(
        ({ field }) => field === "room.number"
      );
      if (roomNumberFilter && roomNumberFilter.value) {
        filters["room.number"] = roomNumberFilter.value;
        searchParams.roomNumber = filters["room.number"];
      }

      const roomNameFilter = items.find(({ field }) => field === "room.name");
      if (roomNameFilter && roomNameFilter.value) {
        filters["room.name"] = roomNameFilter.value;
        searchParams.roomName = filters["room.name"];
      }

      const levelFilter = items.find(({ field }) => field === "level.name");
      if (levelFilter && levelFilter.value) {
        filters.level = levelFilter.value;
        searchParams.level = levelFilter.value;
      }
      const conditionFilter = items.find(({ field }) => field === "condition");
      if (conditionFilter && conditionFilter.value) {
        filters.condition = conditionFilter.value;
        searchParams.condition = filters.condition;
      }

      const installationDateFilter = items.find(
        ({ field }) => field === "installation_date"
      );
      if (installationDateFilter && installationDateFilter.value) {
        if (
          installationDateFilter.operator === "equals" ||
          installationDateFilter.operator === "is"
        ) {
          // Change equals to use $gt start of the year and $lt end of the year
          const year = installationDateFilter.value;
          const startDate = new Date(year, 0, 1).toISOString();
          const endDate = new Date(year, 11, 31).toISOString();
          filters.installation_date = `$gt,${startDate},$lt,${endDate}`;
          searchParams.installation_date = `${operatorToQuery(
            installationDateFilter.operator
          )},${installationDateFilter.value}`;
        } else {
          filters.installation_date = `${operatorToQuery(
            installationDateFilter.operator
          )},${installationDateFilter.value}`;
          searchParams.installation_date = filters.installation_date;
        }
      }

      if (
        installationDateFilter?.operator === "isEmpty" ||
        installationDateFilter?.operator === "isNotEmpty"
      ) {
        filters.installation_date = `${
          installationDateFilter?.operator === "isEmpty" ? "$eq" : "$ne"
        },null`;
        searchParams.installation_date = filters.installation_date;
      }

      // Add filter logic for created_date (date field, like installation_date)
      const createdDateFilter = items.find(
        ({ field }) => field === "created_date"
      );
      if (createdDateFilter && createdDateFilter.value) {
        if (createdDateFilter.operator === "is") {
          // Change equals to use $gte start of the day and $lte end of the day
          let date = createdDateFilter.value;
          if (!(date instanceof Date)) {
            date = new Date(date);
          }
          // Get UTC components from the date to ensure we're working in UTC
          const startDate = new Date(
            Date.UTC(
              date.getUTCFullYear(),
              date.getUTCMonth(),
              date.getUTCDate(),
              0,
              0,
              0,
              0
            )
          );
          const endDate = new Date(
            Date.UTC(
              date.getUTCFullYear(),
              date.getUTCMonth(),
              date.getUTCDate(),
              23,
              59,
              59,
              999
            )
          );
          filters.created_date = `$gte,${startDate.toISOString()},$lte,${endDate.toISOString()}`;
          searchParams.created_date = `${operatorToQuery(
            createdDateFilter.operator
          )},${createdDateFilter.value}`;
        } else {
          let value = createdDateFilter.value;
          // If value is a Date, convert to ISO string
          if (value instanceof Date) {
            value = value.toISOString();
          }
          // Support range queries (e.g., between two dates)
          if (Array.isArray(value) && value.length === 2) {
            const start =
              value[0] instanceof Date ? value[0].toISOString() : value[0];
            const end =
              value[1] instanceof Date ? value[1].toISOString() : value[1];
            filters.created_date = `$gte,${start},$lte,${end}`;
            searchParams.created_date = filters.created_date;
          } else {
            filters.created_date = `${operatorToQuery(
              createdDateFilter.operator
            )},${value}`;
            searchParams.created_date = filters.created_date;
          }
        }
      }

      // Add filter logic for modified_date (date field, like custom date fields)
      const modifiedDateFilter = items.find(
        ({ field }) => field === "modified_date"
      );
      if (modifiedDateFilter && modifiedDateFilter.value) {
        if (modifiedDateFilter.operator === "is") {
          // Change equals to use $gte start of the day and $lte end of the day
          let date = modifiedDateFilter.value;
          if (!(date instanceof Date)) {
            date = new Date(date);
          }
          // Get UTC components from the date to ensure we're working in UTC
          const startDate = new Date(
            Date.UTC(
              date.getUTCFullYear(),
              date.getUTCMonth(),
              date.getUTCDate(),
              0,
              0,
              0,
              0
            )
          );
          const endDate = new Date(
            Date.UTC(
              date.getUTCFullYear(),
              date.getUTCMonth(),
              date.getUTCDate(),
              23,
              59,
              59,
              999
            )
          );
          filters.modified_date = `$gte,${startDate.toISOString()},$lte,${endDate.toISOString()}`;
          searchParams.modified_date = `${operatorToQuery(
            modifiedDateFilter.operator
          )},${modifiedDateFilter.value}`;
        } else {
          let value = modifiedDateFilter.value;
          // If value is a Date, convert to ISO string
          if (value instanceof Date) {
            value = value.toISOString();
          }
          // Support range queries (e.g., between two dates)
          if (Array.isArray(value) && value.length === 2) {
            const start =
              value[0] instanceof Date ? value[0].toISOString() : value[0];

            const end =
              value[1] instanceof Date ? value[1].toISOString() : value[1];

            filters.modified_date = `$gte,${start},$lte,${end}`;
            searchParams.modified_date = filters.modified_date;
          } else {
            filters.modified_date = `${operatorToQuery(
              modifiedDateFilter.operator
            )},${value}`;
            searchParams.modified_date = filters.modified_date;
          }
        }
      }
      const totalCostWithAdjustmentFilter = items.find(
        ({ field }) => field === "costlines.total_cost_with_adjustments"
      );
      if (
        totalCostWithAdjustmentFilter &&
        totalCostWithAdjustmentFilter.value
      ) {
        filters.total_cost_with_adjustments = `${operatorToQuery(
          totalCostWithAdjustmentFilter.operator
        )},${totalCostWithAdjustmentFilter.value}`;
        searchParams.total_cost_with_adjustments =
          filters.total_cost_with_adjustments;
      }
      if (
        totalCostWithAdjustmentFilter?.operator === "isEmpty" ||
        totalCostWithAdjustmentFilter?.operator === "isNotEmpty"
      ) {
        filters.total_cost_with_adjustments = `${operatorToQuery(
          totalCostWithAdjustmentFilter.operator
        )}`;
        searchParams.total_cost_with_adjustments =
          filters.total_cost_with_adjustments;
      }
      const attachmentsFilter = items.find(({ field }) => field === "Details");
      if (attachmentsFilter && attachmentsFilter.value) {
        filters.has_attachments = attachmentsFilter.value;
      } else {
        setAttachmentFilterState(null);
      }
      const customFields = items.filter(({ field }) =>
        field.includes("values.")
      );
      const customFieldFilters: { [key: string]: any } = {};

      // customSearchParams is an array of objects containing name and value
      const customSearchParams = customFields
        .filter(({ value }) => !!value)
        .map(({ field, value }) => {
          // If the field name starts with 'values.' then we want to remove that prefix
          if (field.startsWith("values.")) {
            field = field.replace("values.", "");
          }
          return { name: field, value };
        });
      if (customSearchParams.length > 0) {
        const customFieldSearchParam = JSON.stringify(customSearchParams);
        searchParams.pin_field = customFieldSearchParam;
      }
      customFields.reduce((acc, { field, value, operator }) => {
        // Ignore fields with empty values
        if (!value) return acc;
        const fieldName = field.split("values.")[1];
        const filterKey = fieldName;
        let filterObject = {};
        // if the value is a date we need to convert it to the time in milliseconds
        if (value instanceof Date) {
          value = value.getTime();
        }
        const queryOperator = operatorToQuery(operator);
        switch (operator) {
          case "is":
          case "equals":
          case "notEquals":
            filterObject = { [queryOperator]: value };
            break;
          case "contains":
            filterObject = { [queryOperator]: value, $options: "i" };
            break;
          case "isAnyOf":
            filterObject = { $in: value };
            break;
          default:
            filterObject = { [queryOperator]: value };
            break;
        }
        customFieldFilters[filterKey] = JSON.stringify(filterObject);
        return acc;
      }, {});

      filters.values = customFieldFilters;
      filters.decommissioned = filterModel.items.find(
        ({ field }) => field === "decommissioned"
      )?.value;

      setFilters(filters);
      setSelectedRows([]);
      setSearchParams(searchParams);
    },
    [filterModel.items, setSearchParams]
  );
  const handleFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      setFilterModel(newFilterModel);
      onAssetFilterChange(newFilterModel);
    },
    [setFilterModel, onAssetFilterChange]
  );

  const [searchParamsLoaded, setSearchParamsLoaded] = useState(false);
  useEffect(() => {
    if (!columnsLoaded) return;
    if (!uniquePinTypeFields.length) return;
    if (searchParamsLoaded) return; // This needs to be here to prevent an infinite loop
    const filterItems: any = [];
    function addFilterItem(field: string, operator: string, value: any) {
      filterItems.push({
        field,
        operator: queryToOperator(operator),
        value,
        id: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
      });
    }
    if (searchParams.get("decommissioned") === "true") {
      setShowDecommissioned(true);
    }
    if (searchParams.get("name")) {
      addFilterItem("name", "contains", searchParams.get("name")?.toString());
    }
    if (searchParams.get("building")) {
      const buildingParam = searchParams.get("building")?.toString() || "";
      const [operator, ...value] = buildingParam.split(",");
      if (!value) return;
      if (value.length > 1) {
        addFilterItem("building.name", "isAnyOf", value);
      } else {
        addFilterItem("building.name", operator, value[0]);
      }
    }
    if (searchParams.get("installation_date")) {
      const installationDateParam =
        searchParams.get("installation_date")?.toString() || "";
      const [operator, installationDate] = installationDateParam.split(",");
      if (installationDate === "null") {
        addFilterItem(
          "installation_date",
          operator === "$eq" ? "isEmpty" : "isNotEmpty",
          ""
        );
      } else {
        addFilterItem("installation_date", operator, installationDate);
      }
    }
    // Add support for created_date in search params loader
    if (searchParams.get("created_date")) {
      const createdDateParam =
        searchParams.get("created_date")?.toString() || "";
      const [operator, created_date] = createdDateParam.split(",");
      if (created_date === "null") {
        addFilterItem(
          "created_date",
          operator === "$eq" ? "isEmpty" : "isNotEmpty",
          ""
        );
      } else {
        addFilterItem("created_date", operator, created_date);
      }
    }
    // Add support for modified_date in search params loader
    if (searchParams.get("modified_date")) {
      const modifiedDateParam =
        searchParams.get("modified_date")?.toString() || "";
      const [operator, modified_date] = modifiedDateParam.split(",");
      if (modified_date === "null") {
        addFilterItem(
          "modified_date",
          operator === "$eq" ? "isEmpty" : "isNotEmpty",
          ""
        );
      } else {
        addFilterItem("modified_date", operator, modified_date);
      }
    }
    if (searchParams.get("pin_type")) {
      // Get the pinType _id and the pinField value
      const pinType = JSON.parse(searchParams.get("pin_type") || "{}");
      // Look up pinType name by _id
      // const pinTypeName = pinTypes.find(
      //   (type) => type._id === pinType._id
      // )?.name;
      // addFilterItem("pinType.name", "$in", pinTypeName);
      if (pinType.pin_fields) {
        const pinFields = pinType.pin_fields;
        for (const pinField of pinFields) {
          const pinFieldName = pinFields.find(
            (field: any) => field.name === pinField.name
          )?.name;
          if (pinFieldName) {
            addFilterItem(`values.${pinFieldName}`, "contains", pinField.value);
          }
        }
      }
    }
    if (searchParams.get("level")) {
      addFilterItem("level.name", "contains", searchParams.get("level"));
    }
    if (searchParams.get("pinTypeName")) {
      const pinType = searchParams.get("pinTypeName")?.toString() || "";
      const [operator, ...value] = pinType.split(",");
      if (!value) return;
      if (value.length > 1) {
        addFilterItem("pinType.name", "isAnyOf", value);
      } else {
        addFilterItem("pinType.name", operator, value[0]);
      }
    }
    if (searchParams.get("roomNumber")) {
      const roomNumber = searchParams.get("roomNumber")?.toString() || "";
      addFilterItem("room.number", "contains", roomNumber);
    }
    if (searchParams.get("roomName")) {
      const roomName = searchParams.get("roomName")?.toString() || "";
      addFilterItem("room.name", "contains", roomName);
    }
    if (searchParams.get("pin_field")) {
      const pinFields = JSON.parse(searchParams.get("pin_field") || "[]");
      for (const pinField of pinFields) {
        addFilterItem(
          `values.${pinField.name}`,
          pinField.operator,
          pinField.value
        );
      }
    }
    if (searchParams.get("condition")) {
      addFilterItem(
        "condition",
        "is",
        searchParams.get("condition")?.toString() || ""
      );
    }
    if (searchParams.get("total_cost_with_adjustments")) {
      const totalCostWithAdjustments = searchParams
        .get("total_cost_with_adjustments")
        ?.toString();
      if (!totalCostWithAdjustments) return;
      const [operator, value] = totalCostWithAdjustments.split(",");
      if (operator === "$exists") {
        addFilterItem(
          "costlines.total_cost_with_adjustments",
          value === "true" ? "Is Not Empty" : "Is Empty",
          ""
        );
      } else {
        // const [operator, value] = totalCostWithAdjustments.split(",");
        addFilterItem("costlines.total_cost_with_adjustments", operator, value);
      }
    }
    handleFilterModelChange({
      items: filterItems,
    });
    setSearchParamsLoaded(true);
  }, [
    handleFilterModelChange,
    searchParams,
    searchParamsLoaded,
    setFilterModel,
    columns,
    columnsLoaded,
    location.search,
    uniquePinTypeFields,
  ]);

  // Manage filters
  const handleToggleHeaderFilters = () => {
    setShowHeaderFilters((prev) => !prev);
  };

  const handleColumnVisibilityChange = (newModel: {
    [key: string]: boolean;
  }) => {
    const hasDetailSettingsEnabled = Object.values(detailSettings).some(
      (setting) => setting.state
    );
    setColumnVisibilityModel((prevModel) => {
      const hasChanges = Object.keys(newModel).some(
        (key) => newModel[key] !== prevModel[key]
      );
      if (hasChanges) {
        const exportColumns = Object.keys(newModel)
          .filter((key) => newModel[key] !== false)
          .reduce<string[]>((acc, key) => {
            if ((BaseExportFields as Record<string, any>)[key]) {
              acc.push((BaseExportFields as Record<string, any>)[key]);
            }
            if ((BaseExportFieldIds as Record<string, any>)[key]) {
              acc.push((BaseExportFieldIds as Record<string, any>)[key]);
            }
            if ((BaseExportExtended as Record<string, any>)[key]) {
              // Note this is an array of strings
              acc.push(...(BaseExportExtended as Record<string, any>)[key]);
            } else {
              if (key.startsWith("values.")) {
                // remove the "values." prefix
                acc.push(key.replace("values.", ""));
              }
            }
            return acc;
          }, []);
        setExportColumns(exportColumns);
      }
      setIsColumnChanged(hasChanges);
      return {
        ...(hasChanges ? newModel : prevModel),
        Details: hasDetailSettingsEnabled,
      };
    });
  };

  useEffect(() => {
    // Detect if there any changes in the detailSettings
    // and if there are 1 or more setting enabled, make sure that
    // the Details column is visible
    // and if there are 0 settings enabled, make sure that the Details column is hidden
    const hasDetailSettingsEnabled = Object.values(detailSettings).some(
      (setting) => setting.state
    );
    if (hasDetailSettingsEnabled) {
      setColumnVisibilityModel((prevModel) => {
        return {
          ...prevModel,
          Details: true,
        };
      });
    } else {
      setColumnVisibilityModel((prevModel) => {
        return {
          ...prevModel,
          Details: false,
        };
      });
    }
  }, [detailSettings]);

  function handleRowModesModelChange(rowModesModel: GridRowModesModel): void {
    setRowModesModel(rowModesModel);
  }
  const handleRowEditStart = useCallback(
    (params: GridRowEditStartParams, event: MuiEvent) => {
      const hasOtherRowInEditMode = Object.keys(rowModesModel)
        .filter((key) => key !== params.row._id)
        .some((key) => rowModesModel[key]?.mode === GridRowModes.Edit);
      if (
        !assetPermissions.update ||
        params.reason === GridRowEditStartReasons.printableKeyDown ||
        (params.reason === GridRowEditStartReasons.cellDoubleClick &&
          hasOtherRowInEditMode)
      ) {
        event.defaultMuiPrevented = true;
      }
    },
    [assetPermissions.update, rowModesModel]
  );

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params: GridRowEditStopParams,
    event: MuiEvent
  ) => {
    if (
      params.reason === GridRowEditStopReasons.rowFocusOut ||
      params.reason === GridRowEditStopReasons.enterKeyDown
    ) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleProcessRowUpdate = useCallback(
    async (newValues: WebAssetResponse, oldValues: WebAssetResponse) => {
      if (oldValues.name !== newValues.name) {
        await updateAsset({
          args: {
            buildingId: newValues.building._id,
            assetId: newValues._id,
            asset: {
              name: newValues.name,
            },
          },
        });

        // Update the local cache with the new name and display_name
        // display_name is a virtual property that mirrors the name value
        mutate(
          (current) => {
            if (!current) return;
            const updatedCurrent = current.map((asset) => {
              const data = asset.data.map((item) => {
                if (item._id === newValues._id) {
                  return {
                    ...item,
                    name: newValues.name,
                    display_name: newValues.name,
                  };
                }
                return item;
              });

              return {
                ...asset,
                data,
              };
            });

            return [...updatedCurrent];
          },
          { revalidate: false }
        );

        setOpenToast(true);
        setToastMessage(
          `${oldValues.name}'s name updated from ${oldValues.name} to ${newValues.name}!`
        );
      }

      if (oldValues.level?._id !== newValues.level?._id) {
        await updateAsset({
          args: {
            buildingId: newValues.building._id,
            assetId: newValues._id,
            asset: {
              action: "updateLevel",
              level: newValues.level?._id,
            },
          },
        });

        mutate(
          (current) => {
            if (!current) return;
            const updatedCurrent = current.map((asset) => {
              const data = asset.data.map((item) => {
                if (item._id === newValues._id) {
                  return { ...item, ...{ level: newValues.level } };
                }
                return item;
              });

              return {
                ...asset,
                data,
              };
            });

            return [...updatedCurrent];
          },
          { revalidate: false }
        );
        setOpenToast(true);
        setToastMessage(
          `${oldValues.name}'s floor updated from ${oldValues.level?.name} to ${newValues.level?.name}!`
        );
      }
      function getInstallationYear(date: string | null) {
        return date ? new Date(date).getFullYear().toString() : "None";
      }
      if (
        getInstallationYear(oldValues.installation_date) !==
        getInstallationYear(newValues.installation_date)
      ) {
        const installationDateEpoch = new Date(
          newValues.installation_date
            ? new Date(newValues.installation_date)
            : ""
        ).getTime();

        await updateAsset({
          args: {
            buildingId: newValues.building._id,
            assetId: newValues._id,
            asset: {
              installation_date: installationDateEpoch,
            },
          },
        });

        mutate(
          (current) => {
            if (!current) return;
            const updatedCurrent = current.map((asset) => {
              const data = asset.data.map((item) => {
                if (item._id === newValues._id) {
                  return {
                    ...item,
                    ...{ installation_date: newValues.installation_date },
                  };
                }
                return item;
              });

              return {
                ...asset,
                data,
              };
            });

            return [...updatedCurrent];
          },
          { revalidate: false }
        );
        setOpenToast(true);
        setToastMessage(
          `${
            oldValues.name
          }'s installation date updated from ${getInstallationYear(
            oldValues.installation_date
          )} to ${getInstallationYear(newValues.installation_date)}!`
        );
      }

      // do a json diff of the values object and if there are differences we'll need to update the asset
      const oldValuesString = JSON.stringify(oldValues.values);
      const newValuesString = JSON.stringify(newValues.values);
      if (oldValuesString !== newValuesString) {
        const diff = Object.keys(newValues.values).reduce((acc, key) => {
          if (
            key &&
            oldValues.values[key] &&
            oldValues.values[key].value !== newValues.values[key].value
          ) {
            acc[oldValues.values[key]._id as string] =
              newValues.values[key].value;
          }
          return acc;
        }, {} as { [key: string]: any });

        if (Object.keys(diff).length > 0) {
          await updateAsset({
            args: {
              buildingId: newValues.building._id,
              assetId: newValues._id,
              asset: {
                action: "updateValues",
                values: diff,
              },
            },
          })
            .then(() => {
              setOpenToast(true);
              setToastMessage(`${oldValues.name}'s values updated!`);
            })
            .catch((err) => {
              setRowModesModel((prevModel) => ({
                ...prevModel,
                [oldValues._id]: { mode: GridRowModes.Edit },
              }));
              throw err;
            });

          mutate(
            (current) => {
              if (!current) return;
              const updatedCurrent = current.map((asset) => {
                const data = asset.data.map((item) => {
                  if (item._id === newValues._id) {
                    return {
                      ...item,
                      ...{
                        values: {
                          ...item.values,
                          ...newValues.values,
                        },
                      },
                    };
                  }
                  return item;
                });

                return {
                  ...asset,
                  data,
                };
              });

              return [...updatedCurrent];
            },
            { revalidate: false }
          );
        }
      }

      // Return updated values with display_name synced to name
      // display_name is a virtual property that mirrors the name field
      return {
        ...newValues,
        display_name: newValues.name,
      };
    },
    [mutate, setOpenToast, setToastMessage, setRowModesModel, updateAsset]
  );

  const [clickCount, setClickCount] = React.useState(0);
  const clickTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [selectedRows, setSelectedRows] = React.useState<WebAssetResponse[]>(
    []
  );
  const [selectAllDialogOpen, setSelectAllDialogOpen] = React.useState(false);
  const handleSelectAllDialogOpen = () => {
    setSelectAllDialogOpen(true);
  };
  const handleSelectAllDialogClose = () => {
    setSelectAllDialogOpen(false);
  };
  const handleSelectAllRowsConfirm = () => {
    handleSelectAllRows();
    handleSelectAllDialogClose();
  };
  const handleSelectAllRows = useCallback(async () => {
    let allRows: WebAssetResponse[] = selectedRows;
    while (allRows.length < assetCount) {
      // Fetch additional page of assets and increment pageCount
      const { data: assetRows } = await api.web.getAssets({
        sort: sortModel.map((item) => `${item.field},${item.sort}`).join(","),
        orgId: organization._id,
        decommissioned: filters?.decommissioned,
        name: filters?.name,
        building: filters?.building,
        pinType: filters?.pinType,
        values: filters?.values,
        level: filters?.level,
        hasAttachments: filters?.has_attachments,
        conditionConditionRating: filters?.condition,
        costlinesTotalCostWithAdjustments: filters?.total_cost_with_adjustments,
        installationDate: filters?.installation_date,
        createdDate: filters?.created_date,
        modifiedDate: filters?.modified_date,
        pinTypeName: filters?.["pinType.name"],
        roomName: filters?.["room.name"],
        roomNumber: filters?.["room.number"],
        // pagination
        skip: allRows.length,
        limit: 100,
      });
      allRows = [...allRows, ...assetRows];
    }
    setSelectedRows(allRows);
    // show a toast saying how many rows were selected
    setOpenToast(true);
    setToastMessage(`${allRows.length} rows selected!`);
  }, [selectedRows, assetCount, sortModel, organization._id, filters]);
  const handleSelectionModelChange = (
    newSelectionModel: GridRowSelectionModel
  ) => {
    if (
      newSelectionModel.length < selectedRows.length &&
      selectedRows.length === assetCount
    ) {
      setSelectedRows([]);
      return;
    }
    const newSelected = assets.filter((row) =>
      newSelectionModel.includes(row._id)
    );
    const combinedSelected = [...selectedRows, ...newSelected];

    // Remove selected that are NOT in the new selection model
    const selected = combinedSelected.filter((row) =>
      newSelectionModel.includes(row._id)
    );
    // Remove duplicates based on _id
    const uniqueSelected = selected.filter(
      (row, index, self) => index === self.findIndex((r) => r._id === row._id)
    );
    setSelectedRows(uniqueSelected);

    // If sidebar is enabled and we have selected rows, open the side drawer
    if (sidebarOpen && uniqueSelected.length > 0) {
      setDrawer("side");
    }

    if (newSelected.length === paginationModel.pageSize) {
      handleSelectAllDialogOpen();
    }
  };

  const handleRowClick = (params: any, event: React.MouseEvent) => {
    const count = clickCount + 1;
    setClickCount(count);

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    clickTimeoutRef.current = setTimeout(() => {
      if (clickCount === 1) {
        if (event.altKey || event.ctrlKey || event.metaKey) {
          handleSelectionModelChange([
            ...selectedRows.map((row) => row._id),
            params.row._id,
          ]);
        } else if (event.shiftKey) {
          // Handle shift select
          const selectedRowIds = selectedRows.map((row) => row._id);
          const lastSelectedRowId = selectedRowIds[selectedRowIds.length - 1];
          const lastSelectedRowIndex = assets.findIndex(
            (row) => row._id === lastSelectedRowId
          );
          const clickedRowIndex = assets.findIndex(
            (row) => row._id === params.row._id
          );
          const start = Math.min(lastSelectedRowIndex, clickedRowIndex);
          const end = Math.max(lastSelectedRowIndex, clickedRowIndex);
          const newSelectedRows = assets.slice(start, end + 1);
          setSelectedRows(newSelectedRows);

          // If sidebar is enabled and we have selected rows, open the side drawer
          if (sidebarOpen && newSelectedRows.length > 0) {
            setDrawer("side");
          }
        } else {
          // Handle single select
          setSelectedRows([params.row]);
          if (sidebarOpen) {
            setDrawer("side");
          }
        }
      }
      setClickCount(0);
    }, 500); // Adjust the timeout duration as needed
  };

  // Handler to receive import date(s) from ImportDrawer and update filter/query
  const handleImportDateSelect = (createdDate: Date, endDate: Date) => {
    setFilterModel((prev: any) => {
      // Remove any existing modified_date filter
      const items = prev.items.filter(
        (item: any) => item.field !== "modified_date"
      );
      let filterItem;
      if (createdDate && endDate) {
        filterItem = {
          field: "modified_date",
          operator: "between",
          value: [createdDate, endDate],
          id: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
        };
      } else if (createdDate) {
        filterItem = {
          field: "modified_date",
          operator: "after",
          value: createdDate,
          id: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
        };
      }
      const newModel = {
        ...prev,
        items: [...items, filterItem],
      };
      onAssetFilterChange(newModel);
      return newModel;
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box css={ss.dataGridWrapper}>
        <ActiveViewToolbar
          organizationId={organization._id}
          columnVisibilityModel={columnVisibilityModel}
          initialColumnVisibilityModel={initialColumnVisibilityModel}
          filterModel={filterModel}
          visibleFilterTerms={visibleFilterTerms}
          isColumnChanged={isColumnChanged}
          isFilterChanged={isFilterChanged}
          isColumnOrderChanged={isColumnOrderChanged}
          columnOrder={columnOrder}
          setFilterModel={handleFilterModelChange}
          setColumnVisibilityModel={handleColumnVisibilityChange}
          setColumnOrder={setColumnOrder}
          onResetColumnVisibility={() => setIsColumnChanged(false)}
          onResetColumnOrder={() => setIsColumnOrderChanged(false)}
          setOpenToast={setOpenToast}
          setToastMessage={setToastMessage}
          showDecommissioned={showDecommissioned}
        />
        <Box css={ss.container}>
          <DataGridPro
            initialState={{
              pinnedColumns: {
                left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "name"],
                right: ["Details", "Actions"],
              },
              columns: {
                columnVisibilityModel: initialColumnVisibilityModel,
              },
            }}
            apiRef={apiRef}
            getRowId={(row) => row._id}
            rows={assets}
            columns={columns}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            autoHeight
            rowCount={assetCount}
            loading={
              isLoading ||
              isValidating ||
              isCounting ||
              !(uniquePinTypeFields.length > 0)
            }
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={allowedPageSizes}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            onColumnOrderChange={handleColumnOrderChange}
            filterDebounceMs={700}
            slots={{
              toolbar: () => (
                <GridToolbar
                  showHeaderFilters={showHeaderFilters}
                  onToggleHeaderFilters={handleToggleHeaderFilters}
                  showCustomColumns={showCustomColumns}
                  onToggleCustomColumns={handleCustomColumnsClick}
                  checkboxSetting={checkboxVisible}
                  onToggleCheckboxSetting={handleToggleCheckboxSetting}
                  onExportClick={() => {
                    setDrawer("export");
                  }}
                  onImportClick={() => {
                    setDrawer("import");
                  }}
                  detailSettings={detailSettings}
                  onDetailSettingChange={updateSetting}
                  sidebarSetting={sidebarOpen}
                  onSidebarSettingChange={updateSidebarSetting}
                  showDecommissioned={showDecommissioned}
                  onDecommissionedSettingChange={updateDecommissionedSetting}
                />
              ),
              pagination: () => (
                <ListPagination
                  handleRefresh={() => {
                    mutate();
                  }}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                />
              ),
            }}
            headerFilters={showHeaderFilters}
            onRowClick={handleRowClick}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStart={handleRowEditStart}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={handleProcessRowUpdate}
            onProcessRowUpdateError={(err) => {
              const message = err?.response?.data?.error?.properties
                ? err.response.data.error.properties.errors[0].message
                : err.response.data.error.message;
              setToastMessage(message);
              setOpenToast(true);
            }}
            checkboxSelection={checkboxVisible}
            indeterminateCheckboxAction="deselect"
            keepNonExistentRowsSelected
            rowSelectionModel={selectedRows.map((row) => row._id)}
            onRowSelectionModelChange={(
              newSelectionModel: GridRowSelectionModel
            ) => {
              handleSelectionModelChange(newSelectionModel);
            }}
          />
          {/* </Box> */}
          {selectedRows.length > 0 && sidebarOpen && drawer === "side" ? (
            <Box css={ss.sideDrawerContainer}>
              <SideDrawer
                open={drawer === "side"}
                onClose={() => setDrawer(null)}
                rows={selectedRows}
              />
            </Box>
          ) : null}
          {drawer === "export" ? (
            <Box css={ss.sideDrawerContainer}>
              <ExportDrawer
                open={drawer === "export"}
                onClose={() => setDrawer(null)}
                filters={filters}
                organization={organization}
                exportColumns={exportColumns}
              />
            </Box>
          ) : null}
          {drawer === "import" ? (
            <Box css={ss.sideDrawerContainer}>
              <ImportDrawer
                open={drawer === "import"}
                onClose={() => setDrawer(null)}
                organization={organization}
                onImportSelect={handleImportDateSelect}
              />
            </Box>
          ) : null}
        </Box>
        {currentPreview === "attachments" && (
          <AttachmentsPreview
            asset={attachmentAsset}
            anchorEl={anchorEl}
            onClose={handleAttachmentsPreviewClose}
          />
        )}
        {currentPreview === "location" && (
          <LocationPreview
            asset={locationAsset}
            assets={[locationAsset]}
            anchorEl={anchorEl}
            onClose={handleLocationPreviewClose}
          />
        )}
        <SelectAllDialog
          open={selectAllDialogOpen}
          onClose={handleSelectAllDialogClose}
          onConfirm={handleSelectAllRowsConfirm}
          selectedCount={selectedRows.length}
          totalCount={assetCount}
        />
        <Snackbar
          open={openToast}
          autoHideDuration={5000}
          onClose={(_event: React.SyntheticEvent | Event, reason?: string) => {
            if (reason === "clickaway") {
              return;
            }

            setOpenToast(false);
          }}
          message={toastMessage}
        ></Snackbar>
      </Box>
    </LocalizationProvider>
  );
};
